import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'services/localization';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { isEqual } from 'lodash';
import { api } from 'common/frontend/api';
import * as NotificationActions from 'actions/NotificationActions';
import * as StockSelectors from 'selectors/StockSelectors';
import { DinOptions, getDinOptionsFromShopInfo, ProductDinUpdate } from './utils';
import DinSettings from './DinSettings';
import Spinner from 'common/components/Spinner';
import { ById, UserDetail } from 'common/types';
import { Button, CircularProgress } from '@mui/material';
import SetPageTitle from 'components/Portals/SetPageTitle';

const GeneralSettings = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const activeShop = useSelector(ShopSelectors.activeShopData)!;
	const products = useSelector(StockSelectors.stockProducts);
	const { shopInfo } = activeShop;
	const shopId = shopInfo.id;
	const initialDinOptions = getDinOptionsFromShopInfo(shopInfo);
	const [dinOptions, setDinOptions] = useState<DinOptions>(initialDinOptions);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const hasChanges = !isEqual(initialDinOptions, dinOptions);

	const updateDinProductInfo = async (productDinUpdates: ById<ProductDinUpdate>) => {
		const requiredUserDetailsForDin: UserDetail[] = [
			{
				name: 'weight',
				required: true,
			},
			{
				name: 'height',
				required: true,
			},
			{
				name: 'experienceLevel',
				required: true,
			},
			{
				name: 'age',
				required: true,
			},
		];
		await Promise.all(
			Object.entries(productDinUpdates).map(async ([productId, updateInfo]) => {
				const { type, field } = updateInfo;
				const product = products.data?.find((product) => product.id === productId);
				if (!product) return;
				const newUserDetails =
					type !== 'add'
						? undefined
						: [...(product.userDetails || []), ...requiredUserDetailsForDin].filter(
								(obj, index, arr) => arr.map((mapObj) => mapObj.name).indexOf(obj.name) === index,
						  );
				try {
					await api()
						.products.doc(productId)
						.update({
							...(field === 'din' && { collectDin: type === 'add' ? true : false }),
							...(field === 'soleLength' && {
								collectBootSoleLength: type === 'add' ? true : false,
							}),
							...(newUserDetails && { userDetails: newUserDetails }),
						});
				} catch (e) {}
			}),
		);
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			if (hasChanges) {
				await api().shops.doc(shopId).update({
					dinCollectionMethod: dinOptions.dinCollectionMethod,
					'plugins.din.useDinVariation': dinOptions.useDinVariation,
				});
				await updateDinProductInfo(dinOptions.productDinUpdates);
				setDinOptions({
					...dinOptions,
					productDinUpdates: {},
				});
			}
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.error'),
					variant: 'error',
				}),
			);
		} finally {
			setIsSaving(false);
		}
	};

	if (!!products.loading) {
		<Spinner />;
	}

	return (
		<>
			<SetPageTitle
				title={t('products.catalog', 'Catalog')}
				priority={2}
				styling={{ fluid: true }}
				rightContent={
					<Button
						color="primary"
						variant="contained"
						onClick={handleSave}
						disabled={!hasChanges || isSaving}
					>
						{isSaving ? <CircularProgress size={20} color="inherit" /> : t('common:actions.save')}
					</Button>
				}
			/>

			<DinSettings
				dinOptions={dinOptions}
				setDinOptions={setDinOptions}
				products={products.data ?? []}
			/>
		</>
	);
};

export default GeneralSettings;
